import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Send, Sparkles, X } from 'lucide-react';
import axios from 'axios';
import {
  chatbotApi,
  type ChatActionResponse,
  type ChatConversationDetail,
  type ChatMessage,
} from '@/lib/api';
import { ChatActions, ChatMessageBubble, ChatTypingIndicator, ChatWelcome } from './ChatMessages';

export function ChatConversationView({
  workspaceId,
  conversation,
  isLoading,
  activeId,
  mobileView,
  onClose,
  onBack,
  onSent,
  promptSuggestions,
}: {
  workspaceId: string;
  conversation: ChatConversationDetail | undefined;
  isLoading: boolean;
  activeId: string | null;
  mobileView: 'history' | 'chat';
  onClose: () => void;
  onBack: () => void;
  onSent: (conversationId: string) => void;
  promptSuggestions: string[];
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Actions ride on the send response only (not persisted history), so we
  // hold the latest reply's actions and render them under the last bubble.
  const [lastActions, setLastActions] = useState<ChatActionResponse[]>([]);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const send = useMutation({
    mutationFn: (text: string) =>
      chatbotApi.sendMessage(workspaceId, {
        conversationId: activeId ?? undefined,
        message: text,
      }),
    onSuccess: (resp) => {
      setPendingUserMessage(null);
      setError(null);
      setLastActions(resp.actions ?? []);
      onSent(resp.conversationId);
    },
    onError: (err) => {
      setPendingUserMessage(null);
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const body = err.response?.data as { message?: string; error?: string } | undefined;
        if (status === 402 && body?.error?.startsWith('chatbot')) {
          setError(body.message ?? t('chatbot.errorGeneric'));
          return;
        }
        if (status === 503) {
          setError(t('chatbot.disabled'));
          return;
        }
      }
      setError(t('chatbot.errorGeneric'));
    },
  });

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || send.isPending) return;
    setPendingUserMessage(trimmed);
    setError(null);
    send.mutate(trimmed);
    setInput('');
  };

  // Auto-focus on open + new chat. Auto-scroll on new message.
  useEffect(() => {
    inputRef.current?.focus();
    setLastActions([]);
  }, [activeId]);
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conversation?.messages.length, pendingUserMessage, send.isPending]);

  const messages: ChatMessage[] = conversation?.messages ?? [];
  const showWelcome = !activeId && messages.length === 0 && !pendingUserMessage;

  return (
    <div
      className={`flex min-h-0 w-full min-w-0 flex-1 flex-col md:w-[420px] ${
        mobileView === 'chat' ? 'flex' : 'hidden md:flex'
      }`}
    >
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="text-text-secondary hover:text-text-primary md:hidden"
            aria-label={t('chatbot.back')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Sparkles className="text-accent h-4 w-4" />
          <span className="text-text-primary text-sm font-semibold">{t('chatbot.title')}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-text-secondary hover:text-text-primary rounded-md p-1"
          aria-label={t('chatbot.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={messagesScrollRef}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4"
      >
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="text-text-tertiary h-4 w-4 animate-spin" />
          </div>
        )}
        {showWelcome && (
          <ChatWelcome promptSuggestions={promptSuggestions} onPromptClick={(p) => setInput(p)} />
        )}
        {messages.map((msg, i) => (
          <ChatMessageBubble key={i} message={msg} />
        ))}
        {!send.isPending && !pendingUserMessage && lastActions.length > 0 && (
          <ChatActions actions={lastActions} onNavigate={onClose} />
        )}
        {pendingUserMessage && (
          <ChatMessageBubble
            message={{ role: 'user', content: pendingUserMessage, createdAt: null }}
          />
        )}
        {send.isPending && <ChatTypingIndicator />}
        {error && (
          <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            {error}
          </div>
        )}
      </div>

      <div className="border-border border-t px-3 py-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={t('chatbot.placeholder')}
            rows={2}
            className="border-border bg-card text-text-primary placeholder:text-text-tertiary focus:border-accent w-full resize-none rounded-lg border px-3 py-2 text-sm focus:outline-none"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!input.trim() || send.isPending}
            className="bg-accent flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={t('chatbot.send')}
          >
            {send.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
